import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpenseReportsBox, {
  ExpenseReportsAnalyticsEventProperties,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { createSetIsDeleteExpenseReportsPopupOpen } from 'src/redux/actions/app.actions'
import { selectIsCreateDeleteExpenseReportsPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const SubmitterExpenseReports: React.FC<React.PropsWithChildren> = () => {
  const dispatch = useBackgroundDispatch()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const outOfPocketExpensesQuery = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const gettingOutOfPocketExpensesEnabled = outOfPocketExpensesQuery.isLoading
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    outOfPocketExpensesQuery.data,
  )
  const isDeleteExpenseReportPopupOpen = useSelector(
    selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  )
  const [bulkSelected, setBulkSelected] = useState<boolean>(false)
  const [selectedExpenseReports, setSelectedExpenseReports] = useState<
    ExpenseReportModel[]
  >([])
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    ExpenseReportsAnalyticsEventProperties | undefined
  >(undefined)

  const handleExpenseReportsCheckboxClick = (
    option: ExpenseReportModel,
    allReports: ExpenseReportModel[],
  ): void => {
    if (!selectedExpenseReports.includes(option)) {
      if (selectedExpenseReports.length + 1 === allReports.length) {
        setBulkSelected(true)
      }
      setSelectedExpenseReports((prev) => [...prev, option])
    } else {
      if (bulkSelected) {
        setBulkSelected(false)
      }
      setSelectedExpenseReports((prev) =>
        prev.filter((item) => item !== option),
      )
    }
  }

  const handleBulkExpenseCheckboxClick = (
    allReports: ExpenseReportModel[],
  ): void => {
    bulkSelected
      ? setSelectedExpenseReports([])
      : setSelectedExpenseReports(
          allReports.filter(
            (item) => item.status === ExpenseReportStatus.Draft,
          ),
        )
    setBulkSelected(!bulkSelected)
  }

  const handleDeleteReports = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    dispatch(createSetIsDeleteExpenseReportsPopupOpen(true, expenseReportIds))
  }

  const handleCloseToolbar = (): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }

  useEffect((): void => {
    if (!isDeleteExpenseReportPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isDeleteExpenseReportPopupOpen])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('your expense reports filter applied', {
        report_status: selectedFilters.report_status,
      })
    }
  }, [selectedFilters])

  return gettingOutOfPocketExpensesEnabled ? (
    <LoadingBackgroundPage />
  ) : outOfPocketExpensesEnabled ? (
    <>
      <Helmet>
        <title>{`Your Reports | ${appName}`}</title>
        <meta name="description" content={`Your Reports page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        <ExpenseReportsBox
          selectedExpenseReports={selectedExpenseReports}
          bulkSelected={bulkSelected}
          handleExpenseReportsCheckboxClick={handleExpenseReportsCheckboxClick}
          handleBulkExpenseReportsCheckboxClick={handleBulkExpenseCheckboxClick}
          handleReportBulkAction={handleDeleteReports}
          handleCloseToolbar={handleCloseToolbar}
          allReports={false}
          hasApproverPermissions={false}
          setSelectedFilters={setSelectedFilters}
          context={'submitter'}
        />
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default SubmitterExpenseReports
